import type { ObjectArrayCondition } from "@skilitics/judy";

import type { ConditionBuilder } from "../../../../types";
import { ObjectArrayConditionBuilderSummary } from "./ObjectArrayConditionBuilderSummary";
import type { ObjectArrayConditionFormValues } from "./ObjectArrayConditionForm";
import { ObjectArrayConditionForm } from "./ObjectArrayConditionForm";
import type { ObjectArrayConditionContext } from "./types";

export const objectArrayConditionBuilder: ConditionBuilder<ObjectArrayConditionContext> = {
  typeId: "objectArray",
  defaultCategory: "Activity",
  build: ({
    config: { arrayOperator, objectOperator, value },
    spec: { resultPropertyName, objectPropertyName, objectPropertyType },
    resultProperty,
  }) => {
    if (!(arrayOperator && objectOperator && value !== undefined)) return;

    return {
      type: "objectArray",
      subject: resultProperty(resultPropertyName, "objectArray"),
      operator: arrayOperator,
      condition: {
        type: "boolean",
        resolveType: "condition",
        condition: {
          type: objectPropertyType,
          operator: objectOperator,
          subject: { type: objectPropertyType, resolveType: "objectProperty", name: objectPropertyName },
          object: value,
        },
      },
    } as ObjectArrayCondition;
  },
  getInitialConfig: () => ({}),
  views: {
    summary: ({
      spec: { singularName, options, arrayOperator: specOperator, objectPropertyName, resultPropertyName },
      config: { arrayOperator, objectOperator, value },
    }) => {
      const valueLabel = options?.find((o) => o.value === value)?.label ?? value;

      return (
        <ObjectArrayConditionBuilderSummary
          singularName={singularName}
          arrayOperator={specOperator ?? arrayOperator}
          objectOperator={objectOperator}
          objectPropertyName={objectPropertyName}
          resultPropertyName={resultPropertyName}
          value={valueLabel}
        />
      );
    },
    edit: ({
      spec: { options, arrayOperator: specOperator, objectPropertyType },
      config: { value, arrayOperator, objectOperator },
      onConfigChange,
    }) => {
      const initialValues: ObjectArrayConditionFormValues = {
        arrayOperator: specOperator ?? arrayOperator ?? "",
        objectOperator: objectOperator ?? "",
        value: value ?? "",
      };

      const handleSubmit = (values: ObjectArrayConditionFormValues) => {
        onConfigChange({
          arrayOperator: values.arrayOperator || undefined,
          objectOperator: values.objectOperator || undefined,
          value: values.value,
        });
      };

      return (
        <ObjectArrayConditionForm
          initialValues={initialValues}
          onSubmit={handleSubmit}
          options={options}
          specOperator={specOperator}
          objectPropertyType={objectPropertyType}
        />
      );
    },
  },
};

export type ObjectArrayConditionBuilder = typeof objectArrayConditionBuilder;
